import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";

import {
  getTableRowsPayload,
  type TableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";

import type { HydratedTable, TableMetadata } from "./types";

/**
 * Loads the rows of one extracted table. Returns `undefined` while loading and `null` when the
 * rows could not be loaded. After a save, the previous rows stay until the new ones arrive, so
 * views built on them (like an open editor) don't unmount.
 */
export function useTableRows(table: TableMetadata | undefined): HydratedTable | null | undefined {
  const { data: rowPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.extractedTables.getRowsPayloadRef,
      table ? { tableId: table._id } : "skip"
    )
  );
  const [loaded, setLoaded] = useState<{
    tableId: string;
    payload: TableRowsPayload | null;
  } | null>(null);

  useEffect(() => {
    if (!rowPayloadRef) return;
    const ref = rowPayloadRef as TableRowsPayloadRef;
    let cancelled = false;
    getTableRowsPayload(ref)
      .then((payload) => {
        if (!cancelled) setLoaded({ tableId: ref.tableId, payload });
      })
      .catch((error: unknown) => {
        console.error("Failed to load table rows", error);
        if (!cancelled) setLoaded({ tableId: ref.tableId, payload: null });
      });
    return () => {
      cancelled = true;
    };
  }, [rowPayloadRef]);

  return useMemo(() => {
    if (!table) return undefined;
    if (rowPayloadRef === null) return null;
    if (loaded?.tableId !== table._id) return undefined;
    if (!loaded.payload) return null;
    return { ...table, headers: loaded.payload.headers, rows: loaded.payload.rows };
  }, [table, rowPayloadRef, loaded]);
}
